import { HIDDEN_SELECTOR } from '../utils/keyboard-nav-support';
import Modifier from 'ember-modifier';

export const stripBolding = function (element) {
  let strongElements = element.querySelectorAll('strong');

  Array.from(strongElements).forEach(strong => {
    let textNode = element.ownerDocument.createTextNode(strong.textContent);
    let parentNode = strong.parentNode || strong.parentElement;

    if (!parentNode) return;

    parentNode.replaceChild(textNode, strong);
    parentNode.textContent = parentNode.textContent?.replace(/ +/g, ' ');
  });

  element?.normalize();

  return element?.textContent?.trim();
};

const createWalker = element =>
  element.ownerDocument.createTreeWalker(element, NodeFilter.SHOW_TEXT, null, false);

const getNodes = walker => {
  let allNodes = [];
  let textNode;

  while ((textNode = walker.nextNode())) {
    if (textNode.nodeValue.trim() === '') {
      continue;
    }

    allNodes.push(textNode);
  }

  return allNodes;
};

const processNodes = (allNodes, upperSearchTerm) => {
  let matches = [];

  allNodes.forEach(textNode => {
    let originalNodeValue = textNode.nodeValue;
    let trimmedNodeValue = originalNodeValue.trim();
    let upperText = trimmedNodeValue.toUpperCase();

    if (upperText.includes(upperSearchTerm)) {
      let position = upperText.indexOf(upperSearchTerm);
      let match = {
        offset: originalNodeValue.indexOf(trimmedNodeValue),
        positions: [position],
        textNode,
        trimmedNodeLength: trimmedNodeValue.length,
      };

      while ((position = upperText.indexOf(upperSearchTerm, position + 1)) !== -1) {
        match.positions.push(position);
      }

      matches.push(match);
    }
  });

  return matches;
};

const createAndPopulateStrongElement = ({
  element,
  upperSearchTerm,
  matchText,
  position,
  needsLeadingSpace,
  needsTrailingSpace,
  trimmedLength,
}) => {
  let isAtBeginning = position === 0;
  let isAtEnd = position + upperSearchTerm.length >= trimmedLength;
  let prefix = needsLeadingSpace && !isAtBeginning ? ' ' : '';
  let strongElement = element.ownerDocument.createElement('strong');
  let suffix = needsTrailingSpace && !isAtEnd ? ' ' : '';

  strongElement.style.whiteSpace = 'pre-wrap';
  strongElement.textContent = prefix + matchText + suffix;

  return strongElement;
};

const processSingleNode = (
  element,
  upperSearchTerm,
  { offset, positions, textNode, trimmedNodeLength }
) => {
  let matchLength = upperSearchTerm.length;

  positions.reverse().forEach(position => {
    let actualPosition = offset + position;
    let originalTextContent = textNode.nodeValue;
    let beforeMatchPos = actualPosition - 1;
    let afterMatchPos = actualPosition + matchLength;
    let matchStartNode = textNode.splitText(actualPosition);

    matchStartNode.splitText(Math.min(matchLength, matchStartNode.nodeValue.length));

    let matchText = matchStartNode.nodeValue;
    let needsLeadingSpace = false;
    let needsTrailingSpace = false;

    if (beforeMatchPos >= 0 && actualPosition > 0) {
      needsLeadingSpace = originalTextContent[beforeMatchPos] === ' ' && !matchText.startsWith(' ');
    }

    if (afterMatchPos < originalTextContent.length) {
      needsTrailingSpace = originalTextContent[afterMatchPos] === ' ' && !matchText.endsWith(' ');
    }

    let strongElement = createAndPopulateStrongElement({
      element,
      upperSearchTerm,
      matchText,
      position,
      needsLeadingSpace,
      needsTrailingSpace,
      trimmedNodeLength,
    });

    matchStartNode.parentNode.replaceChild(strongElement, matchStartNode);
  });
};

/**
 * Wraps occurrences of `searchTerm` within `rootElement` in `<strong>` tags.
 * Shared by the `bold-matching-text` modifier and any component (e.g. menus) that
 * needs to highlight matches against DOM elements it does not own in its template.
 */
export function applyBoldMatchingText(rootElement, searchTerm) {
  let element = rootElement?.querySelector('span') ?? rootElement;

  if (!element || !searchTerm || element.classList.contains(HIDDEN_SELECTOR)) return;

  let upperSearchTerm = searchTerm.trim().toUpperCase();

  stripBolding(element);

  let allNodes = getNodes(createWalker(element));

  processNodes(allNodes, upperSearchTerm)
    .reverse()
    .forEach(match => processSingleNode(element, upperSearchTerm, match));
}

export default class BoldMatchingText extends Modifier {
  modify = (element, [searchTerm]) => {
    applyBoldMatchingText(element, searchTerm);
  };
}
